import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import EditorAddBlockButton from './parts/EditorAddBlockButton';
import { 
  Heading2, 
  Heading3, 
  AlignLeft, 
  Image as ImageIcon, 
  ListOrdered, 
  List as ListIcon, 
  Quote, 
  Youtube, 
  AlertTriangle, 
  Search, 
  AlignRight, 
  AlignCenter, 
  Bold, 
  Italic, 
  Underline, 
  Code, 
  Link, 
  SquareAsterisk,
} from 'lucide-react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Badge } from '@/components/ui/badge';

interface BlockInsertionPanelProps {
  onAddBlock?: () => void;
  droppableId: string;
}

type BlockCategory = 'basic' | 'media' | 'formatting' | 'all';
type BlockType = {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: BlockCategory;
};

const BlockInsertionPanel: React.FC<BlockInsertionPanelProps> = ({ onAddBlock, droppableId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [recentBlocks] = useState<string[]>(['paragraph', 'header', 'image', 'quote']);
  
  const blocks: BlockType[] = [
    { 
      id: 'paragraph', 
      name: 'Paragraph', 
      description: 'Start writing with plain text',
      icon: <AlignLeft className="h-5 w-5" />, 
      category: 'basic' 
    },
    { 
      id: 'header', 
      name: 'Heading', 
      description: 'Section heading (H2, H3, H4)',
      icon: <Heading2 className="h-5 w-5" />, 
      category: 'basic' 
    },
    { 
      id: 'subheading', 
      name: 'Subheading', 
      description: 'Smaller section heading (H3)',
      icon: <Heading3 className="h-5 w-5" />, 
      category: 'basic' 
    },
    { 
      id: 'list', 
      name: 'Bulleted List', 
      description: 'Create a bulleted list',
      icon: <ListIcon className="h-5 w-5" />, 
      category: 'basic' 
    },
    { 
      id: 'numbered-list', 
      name: 'Numbered List', 
      description: 'Create a numbered list',
      icon: <ListOrdered className="h-5 w-5" />, 
      category: 'basic' 
    },
    { 
      id: 'quote', 
      name: 'Quote', 
      description: 'Insert a quotation with attribution',
      icon: <Quote className="h-5 w-5" />, 
      category: 'basic' 
    },
    { 
      id: 'callout', 
      name: 'Callout', 
      description: 'Highlight important information',
      icon: <AlertTriangle className="h-5 w-5" />, 
      category: 'basic' 
    },
    
    { 
      id: 'image', 
      name: 'Image', 
      description: 'Upload or embed an image',
      icon: <ImageIcon className="h-5 w-5" />, 
      category: 'media' 
    },
    { 
      id: 'youtube', 
      name: 'YouTube', 
      description: 'Embed a YouTube video',
      icon: <Youtube className="h-5 w-5" />, 
      category: 'media' 
    },
    
    { 
      id: 'align-left', 
      name: 'Align Left', 
      description: 'Align text to the left',
      icon: <AlignLeft className="h-5 w-5" />, 
      category: 'formatting' 
    },
    { 
      id: 'align-center', 
      name: 'Align Center', 
      description: 'Center text alignment',
      icon: <AlignCenter className="h-5 w-5" />, 
      category: 'formatting' 
    },
    { 
      id: 'align-right', 
      name: 'Align Right', 
      description: 'Align text to the right',
      icon: <AlignRight className="h-5 w-5" />, 
      category: 'formatting' 
    },
    { 
      id: 'bold', 
      name: 'Bold', 
      description: 'Make selected text bold',
      icon: <Bold className="h-5 w-5" />, 
      category: 'formatting' 
    },
    { 
      id: 'italic', 
      name: 'Italic', 
      description: 'Italicize selected text',
      icon: <Italic className="h-5 w-5" />, 
      category: 'formatting' 
    },
    { 
      id: 'underline', 
      name: 'Underline', 
      description: 'Underline selected text',
      icon: <Underline className="h-5 w-5" />, 
      category: 'formatting' 
    },
    { 
      id: 'code', 
      name: 'Code', 
      description: 'Format as code',
      icon: <Code className="h-5 w-5" />, 
      category: 'formatting' 
    },
    { 
      id: 'link', 
      name: 'Link', 
      description: 'Add a hyperlink',
      icon: <Link className="h-5 w-5" />, 
      category: 'formatting' 
    },
    { 
      id: 'delimiter', 
      name: 'Delimiter', 
      description: 'Add a section break',
      icon: <SquareAsterisk className="h-5 w-5" />, 
      category: 'formatting' 
    },
  ];
  
  const filteredBlocks = searchQuery 
    ? blocks.filter(block => 
        block.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        block.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : blocks;
  
  const getBlocksByCategory = (category: BlockCategory) => {
    return category === 'all' 
      ? filteredBlocks 
      : filteredBlocks.filter(block => block.category === category);
  };
  
  const handleBlockInsert = (blockId: string) => {
    console.log(`Inserting block: ${blockId}`);
    if (onAddBlock) {
      onAddBlock();
    }
  };
  
  return (
    <div className="h-full flex flex-col bg-card/50 border-r border-border/50">
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-medium">Content Blocks</h3>
          {onAddBlock && (
            <EditorAddBlockButton 
              onAddBlock={onAddBlock} 
              className="ml-2" 
            />
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search blocks..."
            className="pl-8 focus:border-primary/40 focus:ring-primary/30"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {searchQuery && (
        <div className="p-4 border-b border-border/50">
          <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
            Search Results
            <Badge variant="outline" className="ml-2 font-normal">{filteredBlocks.length}</Badge>
          </h4>
          <Droppable droppableId={`${droppableId}-search`}>
            {(provided) => (
              <div 
                className="grid grid-cols-1 gap-2"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {filteredBlocks.length > 0 ? (
                  filteredBlocks.map((block, index) => (
                    <DraggableBlockButton 
                      key={block.id}
                      block={block}
                      index={index}
                      onClick={() => handleBlockInsert(block.id)}
                    />
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No blocks found</p>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}

      {!searchQuery && recentBlocks.length > 0 && (
        <div className="p-4 border-b border-border/50">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Recently Used</h4>
          <Droppable droppableId={`${droppableId}-recent`}>
            {(provided) => (
              <div 
                className="grid grid-cols-1 gap-2"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {recentBlocks.map((blockId, index) => {
                  const block = blocks.find(b => b.id === blockId);
                  if (!block) return null;
                  return (
                    <DraggableBlockButton 
                      key={block.id}
                      block={block}
                      index={index}
                      onClick={() => handleBlockInsert(block.id)}
                    />
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}

      <Tabs defaultValue="basic" className="flex-1">
        <TabsList className="grid grid-cols-3 mx-4 mt-4">
          <TabsTrigger value="basic">Basic</TabsTrigger>
          <TabsTrigger value="media">Media</TabsTrigger>
          <TabsTrigger value="formatting">Format</TabsTrigger>
        </TabsList>
        
        <ScrollArea className="flex-1">
          {['basic', 'media', 'formatting'].map((category) => (
            <TabsContent key={category} value={category} className="p-4 h-[calc(100%-40px)]">
              <Droppable droppableId={`${droppableId}-${category}`}>
                {(provided) => (
                  <div 
                    className="grid grid-cols-1 gap-2"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {getBlocksByCategory(category as BlockCategory).map((block, index) => (
                      <DraggableBlockButton 
                        key={block.id}
                        block={block}
                        index={index}
                        onClick={() => handleBlockInsert(block.id)}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </TabsContent>
          ))}
        </ScrollArea>
      </Tabs>
      
      <div className="p-4 border-t border-border/50 mt-auto">
        <div className="text-xs text-muted-foreground">
          <p>Tip: Drag blocks into the editor or press <kbd className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">+</kbd> to add blocks.</p>
          <p className="mt-1">Press <kbd className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">Ctrl+/</kbd> for keyboard shortcuts.</p>
        </div>
      </div>
    </div>
  );
};

interface BlockButtonProps {
  block: BlockType;
  onClick: () => void;
}

const BlockButton: React.FC<BlockButtonProps> = ({ block, onClick }) => {
  return (
    <Button
      variant="outline"
      className="flex items-center justify-start w-full px-3 py-2 h-auto"
      onClick={onClick}
    >
      <div className="mr-2 text-muted-foreground">
        {block.icon}
      </div>
      <div className="text-left">
        <p className="font-medium">{block.name}</p>
        <p className="text-xs text-muted-foreground">{block.description}</p>
      </div>
    </Button>
  );
};

interface DraggableBlockButtonProps extends BlockButtonProps {
  index: number;
}

const DraggableBlockButton: React.FC<DraggableBlockButtonProps> = ({ block, index, onClick }) => {
  return (
    <Draggable draggableId={`block-${block.id}-${index}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`transition-all duration-200 ${snapshot.isDragging ? 'opacity-70 scale-105 shadow-lg z-50' : ''}`}
          style={provided.draggableProps.style}
        >
          <Button
            variant="outline"
            className="flex items-center justify-start w-full px-3 py-2 h-auto group hover:border-primary/40 hover:bg-accent/30"
            onClick={onClick}
            {...provided.dragHandleProps}
          >
            <div className="mr-2 text-muted-foreground group-hover:text-primary">
              {block.icon}
            </div>
            <div className="text-left">
              <p className="font-medium">{block.name}</p>
              <p className="text-xs text-muted-foreground">{block.description}</p>
            </div>
          </Button>
        </div>
      )}
    </Draggable>
  );
};

export default BlockInsertionPanel;
